import React, { useContext, useState, useMemo, useEffect, useRef } from "react";

import { TenantContext } from "../../../../../../templates/context/TenantContext";
import { useForecastStore } from "../../../../../../viewModel/stores/forecastStore";

import { getMonthName } from "../../../../../../utils/utils";
import { i18n } from "../../../../../../translate/i18n";

import { 
  ColumnLabelCell, 
  MonthSortByButton 
} from "../../../../sharedComponents/analysisModuleSharedComponents.index";

import { Text } from "@aidron/aidron-ds";
import PropTypes from 'prop-types';
import { useShallow } from "zustand/react/shallow";

const { t } = i18n;

const historicColumns = ({ unit }) => {
  return [
    {
      label: t("realized"),
      name: "realized",
      fullLabel: t("realizedSales"),
      type: "demand",
    },
    {
      label: t("partPercent"),
      name: "participationInRealized",
      fullLabel: t("percentParticipationInRealized"),
      type: "%",
    },
  ];
};

export function PredictionsColumnLabelsRow({ actions, ctx, uiState }) {

  const [historicLabelCellHeight, setHistoricLabelCellHeight] = useState(0);

  const { setSortRowsBy, setHierarchyCellHeight } = actions || {};
  const { defineHierarchyToDisplay, historicPredictionsToUse, monthColumns } = ctx || {};
  const {
    freezeColumns,
    sumPreviousColumnsWidth,
    evaluateLastCategoryColumn,
    monthHeaderCellHeight,
    sortRowsBy,
    futureColumns,
  } = uiState || {};

  const { hierarchy } = useContext(TenantContext);

  const { latestForecast } = useForecastStore(
    useShallow((state) => ({
      latestForecast: state.latestForecast,
    }))
  );

  const official = useMemo(() => {
    const seen = new Set();
    return (historicPredictionsToUse || [])
    .filter(historicPrediction => historicPrediction.realized != null && historicPrediction.participationInRealized != null)  
    .filter(historicPrediction => {
        const key = `${historicPrediction.year}-${historicPrediction.month}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
  }, [historicPredictionsToUse]);

  const hierarchyLabelCellRef = useRef(null);

  const cellRightBorder = (index) => {
    return !freezeColumns && index === defineHierarchyToDisplay().length - 1
      ? "cell-right-border"
      : "";
  };

  useEffect(() => {
    if (setHierarchyCellHeight) {
      setHierarchyCellHeight(
        hierarchyLabelCellRef.current?.getBoundingClientRect().height
      );
    }
  }, []);

  const defineLeftCellBorder = (monthIndex, columnIndex) => columnIndex === 0 && (official.length !== 0 || monthIndex !== 0);

  const futureColumnsBorders = useMemo(() => {
    return monthColumns.map((month, index) =>
      futureColumns().map((_, i) => defineLeftCellBorder(index, i))
    );
  }, [monthColumns]);

  const columnRefs = useRef();

  const getMonthRefs = () => {
    if (!columnRefs.current) columnRefs.current = new Map();
    return columnRefs.current;
  };

  const defineColumnRefs = (node, index) => {
    const map = getMonthRefs();
    if (node) map.set(index, node);
    else map.delete(index);
  };

  useEffect(() => {
    if (!columnRefs.current) return;
    const columns = [...columnRefs.current.values()];
    if (!columns.length) return;
    setHistoricLabelCellHeight(columns[0].getBoundingClientRect().height);
  }, [monthHeaderCellHeight]);

  return (
    <>
      <tr>
        <th
          rowSpan="2"
          className={`category-th cell-bottom-border subtitle-cell vertical-align-middle background-color-white padding-sides-030rem z-5 position-sticky 
            ${freezeColumns ? "start-0" : ""}`}
          style={{ top: `${monthHeaderCellHeight}px` }}/>

        {/* Hierarquia de produto - parte superior abaixo de "Produtos" */}
        {defineHierarchyToDisplay().map((level, index) => (
          <th
            rowSpan="2"
            key={`${level}`}
            className={`category-th cell-bottom-border subtitle-cell z-5 background-color-white vertical-align-middle width-12px-on-icon height-12px-on-icon 
              ${evaluateLastCategoryColumn(index)} 
              ${cellRightBorder(index)}`}
            style={{
              position: freezeColumns ? "sticky" : "",
              top: `${monthHeaderCellHeight}px`,
              left: freezeColumns ? `${sumPreviousColumnsWidth(index + 1)}px` : "",
            }}
            ref={index === 0 ? hierarchyLabelCellRef : null}
          >
            <div className="row">
              <div className="col d-flex align-items-center small-arrow-button">
                <p>{i18n.t(`${hierarchy.labels[level]}`)}</p>
              </div>
            </div>
          </th>
        ))}

        {/* Meses presentes no forecast */}
        {official.length > 0 && official.map((month, index) => (
          <th
            ref={(node) => defineColumnRefs(node, index)}
            colSpan="2"
            className={`subtitle-cell vertical-align-middle z-3 ${index ? "cell-left-border" : ""}`}
            style={{ top: `${monthHeaderCellHeight}px` }}
            key={`id-${month?.forecastId}-month-${month?.month}-week-${month?.week}-day-${month?.day}`}
          >
            <div className="d-flex align-items-center justify-content-center small-arrow-button table-text width-12px-on-icon height-12px-on-icon position-relative">
              <Text className="text-center">
                {getMonthName(month?.month.toString())} {month.year}
              </Text>
            </div>
          </th>
        ))}

        {/* Dados do forecast (presente) */}
        {monthColumns.map((month, index) =>
          futureColumns({
            month: month,
            forecastGenerationMonth: latestForecast,
          }).map((column, i) => (
            <ColumnLabelCell
              uiState={{
                column: column,
                upperCellsHeight: monthHeaderCellHeight,
                linesSpan: "2",
                cellLeftBorder: futureColumnsBorders[index][i],
                cellBottomBorder: true,
                renderSortByButton: 
                  <MonthSortByButton
                    actions={{ setSortRowsBy: setSortRowsBy }}
                    ctx={{ monthObj: { ...month, months: [month.month] } }}
                    uiState={{ column: column, sortRowsBy: sortRowsBy }}/>
                }}
              key={`month-${month.predictionId}-${column.type}-${column.label}`}
            />
          ))
        )}
      </tr>

      {/* Dados do forecast (histórico)   */}
      <tr>
        {official.map((month, index) => (
          <React.Fragment key={`historic-${month.year}-${month.month}`}>
            {historicColumns({}).map((column, i) => (
              <ColumnLabelCell
                uiState={{
                  column: column,
                  upperCellsHeight: monthHeaderCellHeight + historicLabelCellHeight,
                  cellLeftBorder: index !== 0 && i === 0,
                  cellTopBorder: true,
                  cellBottomBorder: true,
                  renderSortByButton:
                    <MonthSortByButton
                      actions={{ setSortRowsBy: setSortRowsBy }}
                      ctx={{ monthObj: { ...month, months: [month.month] } }}
                      uiState={{ column: column, sortRowsBy: sortRowsBy }}/>
                  }}
                key={`forecast-${month.forecastId}-column-${column.type}`}
              />
            ))}
          </React.Fragment>
        ))}
      </tr>
    </>
  );
};

PredictionsColumnLabelsRow.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};